
import React, { useContext } from "react";
import { Button, Stack, Typography, Box, Container, useMediaQuery } from "@mui/material";
import { VerifiedUser, ArrowForward, Lock } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import PhoneImage from "../../../images/Access-app.webp";
import Playstore from "../../../svgs/Playstore.svg";
import Appstore from "../../../svgs/Appstore.svg";
import { AuthDialogContext } from "../AuthDialog";

function AccessSignup({ closeDialog }) {
  const { openLoginDialog } = useContext(AuthDialogContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // pt-BR first, fallback to English
  const title = "Crie sua conta Neuro-Access";
  const subtitle = "100% brasileiro, 100% seguro. Torne-se investidor(a) hoje.";
  const info = "Cadastre-se usando o app Neuro-Access. Avisaremos quando sua conta for aprovada.";
  const steps = [
    "Baixe o app Neuro-Access.",
    "Crie sua conta no app.",
    "Volte aqui para entrar.",
  ];

  return (
    <Box sx={{
      minHeight: isMobile ? 0 : 540,
      background: 'radial-gradient(ellipse at 60% 10%, #182c2b 0%, #10181f 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 3, md: 6 },
      px: { xs: 1, md: 2 },
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 5,
      boxShadow: '0 8px 48px 0 #14B88D33, 0 0 0 2px #F5C84C22',
    }}>
      {/* Animated emerald/gold light band */}
      <Box
        component="span"
        sx={{
          position: 'absolute',
          left: '-20%',
          top: 0,
          width: '140%',
          height: 180,
          background: 'linear-gradient(90deg, #14B88D 0%, #F5C84C 100%)',
          filter: 'blur(32px)',
          opacity: 0.16,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <Container maxWidth={false} disableGutters sx={{
        borderRadius: 5,
        background: 'linear-gradient(120deg, #10181f 60%, #182c2b 100%)',
        p: { xs: 2.5, md: 4 },
        position: 'relative',
        zIndex: 2,
        overflow: 'hidden',
        minWidth: { xs: 320, md: 600 },
        maxWidth: 720,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 3, md: 6 },
      }}>
        {/* Left: Device preview */}
        <Box sx={{ flex: '0 0 220px', minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: { xs: 2, md: 0 } }}>
          <Box sx={{ position: 'relative', width: 160, height: 320, borderRadius: 8, overflow: 'hidden', boxShadow: '0 6px 40px 0 #14B88D33, 0 0 0 2px #F5C84C22', mb: 1.5, background: 'rgba(20,184,141,0.07)' }}>
            <img src={PhoneImage} alt="App Neuro-Access: tela de cadastro" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 16, filter: 'brightness(1) contrast(1.12)' }} loading="lazy" />
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg,rgba(20,184,141,0.10) 0%,rgba(245,200,76,0.10) 100%)', pointerEvents: 'none' }} />
          </Box>
          <Typography variant="caption" sx={{ color: '#e8faf6', textAlign: 'center', fontWeight: 500, letterSpacing: 0.1, px: 1.5 }}>
            Baixe o app <span style={{ color: '#14B88D', fontWeight: 700 }}>Neuro-Access</span> para criar sua conta
          </Typography>
        </Box>
        {/* Right: Signup narrative, steps, actions */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', py: { xs: 1, md: 3 }, px: { xs: 0, md: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <VerifiedUser sx={{ color: '#14B88D', fontSize: 28, mr: 0.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 900, color: '#F5C84C', letterSpacing: 1.2, textShadow: '0 2px 12px #052C2288' }}>
              Neuro Exchange Brasil
            </Typography>
          </Stack>
          <Typography variant="subtitle1" sx={{ color: '#e8faf6', fontWeight: 600, mb: 1, textAlign: 'left', letterSpacing: 0.2 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#b6e2d6', mb: 2, textAlign: 'left', fontWeight: 400 }}>
            {subtitle}
          </Typography>
          <Typography variant="body2" sx={{ color: '#F5C84C', mb: 2, textAlign: 'left', fontWeight: 500 }}>
            {info}
          </Typography>
          <Stack spacing={1.2} sx={{ mb: 2, width: '100%' }}>
            {steps.map((step, idx) => (
              <Stack direction="row" alignItems="center" spacing={1} key={idx}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(90deg,#14B88D 0%,#F5C84C 100%)', color: '#052C22', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, mr: 1 }}>{idx + 1}</Box>
                <Typography variant="body2" sx={{ color: '#e8faf6', fontWeight: 500 }}>{step}</Typography>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mb: 2, width: '100%' }}>
            <a
              href="https://play.google.com/store/apps/details?id=com.tag.NeuroAccess"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', borderRadius: 8, boxShadow: '0 2px 12px 0 #14B88D22', background: 'rgba(20,184,141,0.10)', padding: 0 }}
            >
              <img src={Playstore} alt="Google Play" style={{ height: 44, width: 'auto', display: 'block', borderRadius: 8 }} />
            </a>
            <a
              href="https://apps.apple.com/us/app/neuro-access/id6446863270"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', borderRadius: 8, boxShadow: '0 2px 12px 0 #F5C84C22', background: 'rgba(245,200,76,0.10)', padding: 0 }}
            >
              <img src={Appstore} alt="App Store" style={{ height: 44, width: 'auto', display: 'block', borderRadius: 8 }} />
            </a>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5, width: '100%' }}>
            <Button
              variant="outlined"
              onClick={closeDialog}
              sx={{
                borderColor: '#14B88D',
                color: '#e8faf6',
                fontWeight: 700,
                letterSpacing: 1.1,
                py: 1.2,
                borderRadius: 2,
                flex: 1,
                fontSize: 17,
                '&:hover': { borderColor: '#F5C84C', color: '#F5C84C', background: 'rgba(245,200,76,0.04)' },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={openLoginDialog}
              sx={{
                background: 'linear-gradient(90deg,#14B88D 0%,#F5C84C 100%)',
                color: '#052C22',
                fontWeight: 900,
                letterSpacing: 1.2,
                py: 1.2,
                borderRadius: 2,
                boxShadow: '0 4px 20px -2px rgba(20,184,141,0.45)',
                flex: 1,
                fontSize: 17,
                '&:hover': { background: 'linear-gradient(90deg,#13a57e 0%,#e5b647 100%)' },
              }}
              endIcon={<ArrowForward sx={{ fontSize: 22, ml: 0.5 }} />}
            >
              Entrar
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ mt: 3, mb: 0.5 }}>
            <Lock sx={{ color: '#14B88D', fontSize: 20, mr: 0.5 }} />
            <Typography variant="caption" sx={{ color: '#b6e2d6', fontWeight: 500, letterSpacing: 0.2 }}>
              Seus dados nunca são compartilhados. Proteção máxima, tecnologia brasileira.
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default AccessSignup;
